import { Component, type ReactNode, type ErrorInfo } from 'react';
import { Button } from '@/components/ui';
import { errorReporting } from '@/services/error-reporting.service';

// ============================================================================
// TYPES
// ============================================================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ErrorBoundary Component
 * 
 * Captura errores de React en cualquier parte del árbol de componentes,
 * registra esos errores y muestra una interfaz de respaldo.
 * 
 * @example
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    errorReporting.report(error, { componentStack: errorInfo.componentStack ?? undefined });
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md space-y-6 text-center">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">
                Algo salió mal
              </h1>
              <p className="text-foreground-secondary">
                Lo sentimos, ha ocurrido un error inesperado.
              </p>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <div className="rounded-lg bg-error-subtle p-4 text-left">
                <p className="font-mono text-sm text-foreground">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="primary"
                fullWidth
                onClick={this.handleReset}
              >
                Intentar de nuevo
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => window.location.href = '/'}
              >
                Ir al inicio
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
